/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: phase (S1/S2 alignment) risk scoring and review gating
// (core-viewer.js 3881-3994). phaseSwappedPeaks (3875-3880) is dead in the
// prototype and dropped.

import { analysisQuality } from "./analysis-quality"
import { visualAcousticActivityStats } from "./evidence"

export function phaseAlternateActivity(activity) {
  if (!activity) return null
  const features = activity.features
    ? {
        ...activity.features,
        sys_cycle_pct: activity.features.dia_cycle_pct,
        dia_cycle_pct: activity.features.sys_cycle_pct,
        sys_mean: activity.features.dia_mean,
        dia_mean: activity.features.sys_mean,
        sys_peak: activity.features.dia_peak,
        dia_peak: activity.features.sys_peak,
        sys_peak_count: activity.features.dia_peak_count,
        dia_peak_count: activity.features.sys_peak_count,
        sys_peaks_per_cycle: activity.features.dia_peaks_per_cycle,
        dia_peaks_per_cycle: activity.features.sys_peaks_per_cycle,
        sys_energy_share: activity.features.dia_energy_share,
        dia_energy_share: activity.features.sys_energy_share,
      }
    : activity.features
  return {
    ...activity,
    purpose: "Alternate phase presentation of the same phase-linked cycles.",
    sysMean: activity.diaMean,
    sysPeak: activity.diaPeak,
    sysCyclePct: activity.diaCyclePct,
    diaMean: activity.sysMean,
    diaPeak: activity.sysPeak,
    diaCyclePct: activity.sysCyclePct,
    features,
    phaseWindows: (activity.phaseWindows || []).map((window) => ({
      ...window,
      phase: window.phase === "sys" ? "dia" : window.phase === "dia" ? "sys" : window.phase,
    })),
  }
}

export function phaseRiskConfidence(ctx) {
  const metrics = ctx.payload.metrics || {}
  const current = visualAcousticActivityStats(ctx, false)
  const swapped = phaseAlternateActivity(current)
  const currentSys = current ? current.sysCyclePct : null
  const currentDia = current ? current.diaCyclePct : null
  const currentMax = current ? Math.max(current.sysCyclePct || 0, current.diaCyclePct || 0) : null
  const swappedSys = swapped ? swapped.sysCyclePct : null
  const swappedDia = swapped ? swapped.diaCyclePct : null
  const swappedMax = swapped ? Math.max(swapped.sysCyclePct || 0, swapped.diaCyclePct || 0) : null
  const swappedSysGain = swappedSys !== null && currentSys !== null ? swappedSys - currentSys : null
  const reasons = []
  let points = 0
  if (String(metrics.quality || "").toUpperCase() === "LOW") {
    points += 2
    reasons.push("low_guide_quality")
  }
  if (metrics.anchor_hypothesis === "S2-chain") {
    points += 1
    reasons.push("s2_chain_selected")
  }
  if (typeof swappedSysGain === "number" && swappedSysGain >= 25) {
    points += 1
    reasons.push("swapped_sys_gain_ge_25")
  }
  if (typeof swappedMax === "number" && typeof currentMax === "number" && swappedMax >= 75 && swappedMax > currentMax) {
    points += 1
    reasons.push("swapped_max_crosses_threshold")
  }
  if (typeof metrics.coverage === "number" && metrics.coverage < 0.8) {
    points += 1
    reasons.push("low_coverage")
  }
  if (typeof metrics.s1s2_amp_ratio === "number" && metrics.s1s2_amp_ratio >= 0.8 && metrics.s1s2_amp_ratio <= 1.25) {
    points += 1
    reasons.push("s1_s2_amplitude_similar")
  }
  if (typeof metrics.sd_ratio === "number" && metrics.sd_ratio >= 0.75 && metrics.sd_ratio <= 1.15) {
    points += 1
    reasons.push("sys_dia_timing_ambiguous")
  }
  const tier = points >= 4 ? "High" : points >= 2 ? "Review" : "Low"
  return {
    purpose: "HCP/research phase confidence only; not an abnormality detector and not patient-facing.",
    tier,
    risk_score: Math.min(1, points / 5),
    guide_confidence: Math.max(0, 1 - Math.min(1, points / 5)),
    points,
    reasons,
    selected_chain: metrics.anchor_hypothesis || null,
    guide_quality: metrics.quality || null,
    coverage: typeof metrics.coverage === "number" ? metrics.coverage : null,
    s1s2_amp_ratio: typeof metrics.s1s2_amp_ratio === "number" ? metrics.s1s2_amp_ratio : null,
    sd_ratio: typeof metrics.sd_ratio === "number" ? metrics.sd_ratio : null,
    current: current && {
      cycles: current.cycles,
      sys_cycle_pct: currentSys,
      dia_cycle_pct: currentDia,
      max_cycle_pct: currentMax,
    },
    swapped: swapped && {
      cycles: swapped.cycles,
      sys_cycle_pct: swappedSys,
      dia_cycle_pct: swappedDia,
      max_cycle_pct: swappedMax,
      sys_gain_vs_current: swappedSysGain,
    },
  }
}

export function phaseReviewNeeded(ctx, quality = analysisQuality(ctx)) {
  const risk = phaseRiskConfidence(ctx)
  const reasons = risk && Array.isArray(risk.reasons) ? risk.reasons : []
  const materialAlternate =
    reasons.includes("swapped_sys_gain_ge_25") || reasons.includes("swapped_max_crosses_threshold")
  return !!(quality && (quality.visualEnergyLimited || quality.visualEnergyPhaseLimited)) || materialAlternate
}
